import * as React from 'react'

import Loader from 'react-loader-spinner'

import Breadcrumb from 'components/breadcrumb/Breadcrumb'
import Banner from 'components/banner/Banner'
import SurveyBanner from 'components/banner/SurveyBanner'
import TitleHelmet from 'components/title/TitleHelmet'

import useI18n from 'i18n/useI18n'

import useReducer from 'store/useReducer'
import * as userStore from 'store/user/user'
import * as siteStore from 'sites/store/siteStore'
import * as bannersStore from 'banners/store'

import styled from 'theme/styled-components'
import useTheme from 'theme/useTheme'

import { windowSizeBreakpoints } from 'utils/breakpoints'
import useNavigation from 'utils/navigation/useNavigation'
import Logger from 'utils/Logger'

import api from './api'
import LockerItem from './LockerItem'
import analytics, { analyticsKeys } from 'utils/analytics'

const LockerScreen = () => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const [Theme] = useTheme()

  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [lockers, setLockers] = React.useState<LockerDetail[]>([])
  const [isShareLoading, setIsShareLoading] = React.useState<boolean[]>([])

  const user = useReducer(userStore.store, (s) => s.user)
  const site = useReducer(siteStore.store, (s) => s.site)
  const banners = useReducer(bannersStore.store, (s) => s.banners)
  const mySites = useReducer(siteStore.store, (s) => s.mySites)

  const showedBanner = React.useMemo(() => banners.find((banner) => !!banner.feature && banner.feature === 'LOCKER'), [
    banners,
  ])

  const findFeature = React.useMemo(() => site?.features.find((f) => f.type === 'LOCKER'), [site])

  const refreshLockers = () => {
    if (user && site) {
      api
        .getLockers(user.type, site?.id)
        .then((res) => res.response && setLockers(res.response))
        .catch(Logger.error)
        .finally(() => {
          setIsShareLoading(lockers.map(() => false))
          setIsLoading(false)
        })
    }
  }

  React.useEffect(() => {
    if (user) {
      analytics.track({ screenName: analyticsKeys.locker, userType: user.type, currentSite: site, mySites })
    }
    refreshLockers()
  }, [user, site])

  return (
    <>
      <TitleHelmet title={i18n.t('screens.locker.title')} />
      <Breadcrumb screen="locker" path="locker" navigation={navigation} />

      <MainContainer>
        {!!showedBanner && <Banner banner={showedBanner} marginRight={20} />}
        <SurveyBanner findFeature={findFeature} />
        <Title>{i18n.t('screens.locker.title')}</Title>
        <Subtitle>{i18n.t(`screens.locker.${!isLoading && lockers.length === 0 ? 'noLocker' : 'subtitle'}`)}</Subtitle>
        {isLoading ? (
          <Loader type="TailSpin" color={Theme.colors.blue} />
        ) : (
          <LockersContainer>
            {lockers.length > 0 &&
              lockers.map((locker, index) => (
                <LockerItem
                  key={locker.id}
                  refreshLockers={refreshLockers}
                  locker={locker}
                  siteId={site?.id}
                  userType={user?.type}
                  isShareLoading={isShareLoading[index]}
                  setIsShareLoading={(isLoading) => {
                    const newIsShareLoading = [...isShareLoading]
                    newIsShareLoading[index] = isLoading
                    setIsShareLoading(newIsShareLoading)
                  }}
                />
              ))}
          </LockersContainer>
        )}
      </MainContainer>
    </>
  )
}

export default LockerScreen

const MainContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-bottom: 60px;
  margin: 0px 90px;
  @media only screen and (max-width: ${windowSizeBreakpoints.big}px) {
    margin: 0px 60px;
  }
  @media only screen and (max-width: ${windowSizeBreakpoints.mediumBig}px) {
    margin: 0px 32px;
  }
  @media only screen and (max-width: ${windowSizeBreakpoints.small}px) {
    margin: 0px 12px;
  }
`

const Title = styled.p`
  ${(props) => props.theme.fonts.title};
  margin-top: 40px;
  margin-bottom: 0;
`

const Subtitle = styled.p`
  ${(props) => props.theme.fonts.body};
  margin-bottom: 30px;
`

const LockersContainer = styled('div')`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  max-width: 1240px; /* 600px * 2 + 40px gap */
  margin-bottom: 20px;

  @media only screen and (max-width: ${windowSizeBreakpoints.mediumBig}px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin: 0 20px 20px;
  }
`
